import { BroadcastChannel, createLeaderElection, LeaderElector } from 'broadcast-channel';
import { Logger } from '../lib/logger';
import { LoggerNames } from '../lib/constants';
import { ChannelName, Service } from './service';
import { isBrowser } from '../lib/browser';
import { AuthSdkError } from '../lib/auth-sdk-error';

type OnLeaderFn = () => Promise<void>;
export interface LeaderElectionServiceOptions {
  electionChannelName: ChannelName;
  onLeader?: OnLeaderFn;
}

export class LeaderElectionService implements Service {
  private readonly logger = Logger.createLogger(LoggerNames.LEADER_ELECTION);
  private channel?: BroadcastChannel;
  private elector?: LeaderElector;
  private started = false;
  constructor(private readonly options: LeaderElectionServiceOptions) {
    this.onLeader = this.onLeader.bind(this);
    this.onLeaderDuplicate = this.onLeaderDuplicate.bind(this);
  }

  private async onLeader() {
    await this.options.onLeader?.();
  }

  private async onLeaderDuplicate() {
    this.logger.debug('Duplicate leader detected');
  }

  public isLeader() {
    return Boolean(this.elector?.isLeader);
  }

  public async hasLeader() {
    return Boolean(await this.elector?.hasLeader());
  }

  async start() {
    if (!this.canStart()) {
      this.logger.warn('Leader election cannot be started or is already running');
      return;
    }

    const { electionChannelName } = this.options;
    try {
      this.channel = new BroadcastChannel(electionChannelName);
    } catch (err) {
      this.logger.debug(err);
      throw new AuthSdkError('Failed to create channel. LeaderElection is not supported in this browser');
    }
    this.elector = createLeaderElection(this.channel);
    this.elector.onduplicate = this.onLeaderDuplicate;
    this.elector.awaitLeadership().then(this.onLeader);
    this.started = true;
  }

  async stop() {
    if (!this.started) {
      this.logger.warn('Leader election already stopped');
      return;
    }

    if (this.elector) {
      await this.elector.die();
      this.elector = undefined;
    }
    if (this.channel) {
      //  Fix closed channel error
      (this.channel as any).postInternal = () => Promise.resolve();
      await this.channel.close();
      this.channel = undefined;
    }

    this.started = false;
  }

  public isStarted() {
    return this.started;
  }

  canStart(): Boolean {
    return !this.started && isBrowser();
  }

  /**
   * Exists to satisfy the Service interface.
   */
  requiresLeadership(): boolean {
    return false;
  }
}
